import React, { useState } from "react";
import { Helmet } from "react-helmet";
import "./styles.scss";

/**
 * SeoContent
 *
 * Static, crawler-friendly content block for the homepage. Everything here is
 * hardcoded (no API dependency) so it is captured in the prerendered HTML that
 * react-snap generates at build time. It provides:
 *   - Semantic headings (<h1>/<h2>/<h3>) so search engines and AI can build a
 *     structural outline of the page. (#2)
 *   - A 300-400 word descriptive paragraph block with real vocabulary and bank
 *     names for text-to-code ratio and long-tail keyword matching. (#3, #4)
 *   - An FAQ section using <h3> questions + <p> answers, plus a JSON-LD
 *     FAQPage schema for voice assistants and AI answer engines. (#5)
 *
 * NOTE ON CRAWLABILITY: FAQ answers are always rendered in the DOM (not
 * unmounted when "closed"). We only toggle an `is-open` class that animates
 * height via CSS, so react-snap's prerendered HTML still contains full
 * answer text for crawlers even though sighted users see a collapsed
 * accordion.
 */

const PARTNER_BANKS = [
  "Commercial Bank",
  "Hatton National Bank (HNB)",
  "Sampath Bank",
  "HSBC",
  "Nations Trust Bank (Amex)",
  "Bank of Ceylon (BOC)",
  "People's Bank",
  "Seylan Bank",
  "DFCC Bank",
  "National Development Bank (NDB)",
  "Pan Asia Bank",
  "Union Bank",
  "Cargills Bank",
];

const FAQS = [
  {
    question: "What is CreditCardOffers.lk?",
    answer:
      "CreditCardOffers.lk is a free directory of credit card offers, deals, and promotions available in Sri Lanka. It brings together discounts, cashback, and rewards from leading banks into one place so you can compare them quickly.",
  },
  {
    question: "Which banks' credit card offers are listed?",
    answer:
      "We list offers from Sri Lanka's major banks, including Commercial Bank, Hatton National Bank (HNB), Sampath Bank, HSBC, Nations Trust Bank (Amex), Bank of Ceylon (BOC), People's Bank, Seylan Bank, DFCC Bank and more.",
  },
  {
    question: "What types of offers can I find?",
    answer:
      "You can find supermarket discounts, hotel and dining deals, travel and airline rewards, cashback promotions, buy-now-pay-later instalment plans, and seasonal festival offers from participating merchants and banks.",
  },
  {
    question: "How often are the credit card offers updated?",
    answer:
      "New offers are added and expired promotions are removed regularly, so the deals you see reflect current bank and merchant campaigns in Sri Lanka.",
  },
  {
    question: "Is CreditCardOffers.lk free to use?",
    answer:
      "Yes. Browsing and comparing credit card offers on CreditCardOffers.lk is completely free. You do not need an account to view the latest deals.",
  },
];

// Cycle the same three brand card colors used on "Why Choose Us"
// (green / purple / amber) across the FAQ items, so the accordion reads as
// part of the same design system rather than a bolted-on generic component.
const ACCENTS = ["accent-green", "accent-purple", "accent-amber"];

const Index = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const toggle = (index) => {
    setOpenIndex((current) => (current === index ? -1 : index));
  };

  return (
    <section
      className="cco__seo-content"
      aria-label="About Credit Card Offers in Sri Lanka"
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Soft blob accents echo the leaf shapes used elsewhere on the site */}
      <span className="cco__seo-blob cco__seo-blob--one" aria-hidden="true" />
      <span className="cco__seo-blob cco__seo-blob--two" aria-hidden="true" />

      <div className="cco__seo-intro">
        <h1 className="cco__seo-title">
          Credit Card Offers in{" "}
          <span className="cco__seo-title-accent">Sri Lanka</span>
        </h1>

        <p>
          <>
            <span className={"mima__tww-creditcard"}>creditcardoffers</span>
            <span className={"mima__tww-lk"}>.lk</span>
          </>{" "}
          is Sri Lanka's dedicated directory for credit card offers, deals, and
          promotions. Whether you are looking for supermarket discounts, hotel
          and dining deals, travel rewards, or cashback on everyday spending, we
          bring together the latest promotions from leading banks into one
          easy-to-browse place. Instead of visiting each bank's website
          separately, you can compare live offers side by side and pick the card
          that gives you the most value.
        </p>
        <p>
          Our platform features credit card promotions from{" "}
          {PARTNER_BANKS.slice(0, -1).join(", ")}, and {PARTNER_BANKS.slice(-1)}
          . These include seasonal festival campaigns, buy-now-pay-later
          instalment plans, airline and hotel partnerships, and
          merchant-specific cashback deals. Each offer listing shows you the
          participating bank, the offer category, and the key details you need
          before you spend, so you always know which credit card unlocks the
          best discount at your favourite supermarkets, restaurants, hotels, and
          online stores across Sri Lanka.
        </p>
        <p>
          Offers are refreshed regularly as banks and merchants launch new
          campaigns and older promotions expire, so the deals you see reflect
          what is genuinely available right now. Browsing is completely free and
          no account is required to view the latest credit card offers. Save
          time, spend smarter, and make the most of your credit card by
          discovering every deal in Sri Lanka in one place at
          CreditCardOffers.lk.
        </p>

        <ul className="cco__seo-banks" aria-label="Participating banks">
          {PARTNER_BANKS.map((bank) => (
            <li key={bank}>{bank}</li>
          ))}
        </ul>
      </div>

      <div className="cco__seo-faq">
        <h2>
          Frequently Asked{" "}
          <span className="cco__seo-title-accent">Questions</span>
        </h2>

        <div className="cco__seo-faq-list">
          {FAQS.map((faq, index) => {
            const isOpen = openIndex === index;
            const panelId = `cco-faq-panel-${index}`;
            const buttonId = `cco-faq-button-${index}`;
            const accent = ACCENTS[index % ACCENTS.length];
            return (
              <div
                className={`cco__seo-faq-item ${accent}${isOpen ? " is-open" : ""}`}
                key={faq.question}
              >
                <h3 className="cco__seo-faq-heading">
                  <button
                    type="button"
                    className="cco__seo-faq-trigger"
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggle(index)}
                  >
                    <span className="cco__seo-faq-question">
                      {faq.question}
                    </span>
                    <span className="cco__seo-faq-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20" width="14" height="14">
                        <path
                          d="M2 6l8 8 8-8"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                </h3>
                <div
                  className="cco__seo-faq-panel"
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                >
                  <div className="cco__seo-faq-panel-inner">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Index;
